import { assignedAgencies } from './agencyAccess';
import type { ShigoUser } from './model';
import { hasShigoPermission, ShigoPermission, ShigoRole } from './permissions';

export enum ShigoDashboardContext {
  Main = 'MAIN',
  SuperAdmin = 'SUPER_ADMIN',
  Publisher = 'PUBLISHER',
  Agency = 'AGENCY',
  Selection = 'SELECTION',
}

type ShigoOwnedDashboardContext =
  | ShigoDashboardContext.Main
  | ShigoDashboardContext.SuperAdmin
  | ShigoDashboardContext.Publisher
  | ShigoDashboardContext.Agency;

export type ShigoDashboardDestination =
  | { context: ShigoDashboardContext.Main; to: '/' }
  | { context: ShigoDashboardContext.SuperAdmin; to: '/' }
  | { context: ShigoDashboardContext.Selection; to: '/login' }
  | { context: ShigoDashboardContext.Agency; to: '/a/$agencyId'; params: { agencyId: string } }
  | {
      context: ShigoDashboardContext.Publisher;
      to: '/p/$publisherId' | '/p/$publisherId/reporting/report-explorer';
      params: { publisherId: string };
    };

export function resolvePostLoginDestination(user: ShigoUser): ShigoDashboardDestination {
  const ownedContext = resolveOwnedDashboardContext(user);
  if (ownedContext === ShigoDashboardContext.SuperAdmin) {
    return { context: ShigoDashboardContext.SuperAdmin, to: '/' };
  }

  const canOpenMain = canOpenMainDashboard(user);
  const canOpenPublisher = canOpenPublisherDashboard(user);
  const publisherIds = canOpenPublisher ? user.publisherIds : [];

  const agencies = assignedAgencies(user);
  if (publisherIds.length + agencies.length + Number(canOpenMain) > 1) {
    return { context: ShigoDashboardContext.Selection, to: '/login' };
  }
  const agency = agencies[0];
  if (agency) {
    return {
      context: ShigoDashboardContext.Agency,
      to: '/a/$agencyId',
      params: { agencyId: agency.id },
    };
  }
  const publisherId = publisherIds[0];
  if (publisherId) {
    return {
      context: ShigoDashboardContext.Publisher,
      to: publisherDashboardPath(user.permissions),
      params: { publisherId },
    };
  }

  return { context: ShigoDashboardContext.Main, to: '/' };
}

export function publisherDashboardPath(permissions: readonly string[]) {
  return hasShigoPermission(permissions, ShigoPermission.PublisherDashboardHomeRead)
    ? ('/p/$publisherId' as const)
    : ('/p/$publisherId/reporting/report-explorer' as const);
}

export function resolveOwnedDashboardContext(user: ShigoUser): ShigoOwnedDashboardContext | null {
  if (user.roles.includes(ShigoRole.SuperAdmin)) return ShigoDashboardContext.SuperAdmin;
  if (user.roles.some((role) => mainDashboardRoles.has(role))) return ShigoDashboardContext.Main;
  if (user.roles.includes(ShigoRole.Publisher)) return ShigoDashboardContext.Publisher;
  if (user.roles.includes(ShigoRole.Agency)) return ShigoDashboardContext.Agency;
  return null;
}

const mainDashboardRoles = new Set<string>([
  ShigoRole.Admin,
  ShigoRole.AdminReadOnly,
  ShigoRole.User,
  ShigoRole.UserReadOnly,
  ShigoRole.Advertiser,
  ShigoRole.AdvertiserReadOnly,
]);

export function publisherIdFromPath(pathname: string) {
  const match = /^\/p\/([^/]+)(?:\/|$)/.exec(pathname);
  return match?.[1] ?? null;
}

export function canAccessAssignedPublisher(user: ShigoUser, pathname: string) {
  const publisherId = publisherIdFromPath(pathname);
  return publisherId === null || user.publisherIds.includes(publisherId);
}

export function canOpenMainDashboard(user: ShigoUser | undefined) {
  return Boolean(
    user &&
    resolveOwnedDashboardContext(user) === ShigoDashboardContext.Main &&
    hasShigoPermission(user.permissions, ShigoPermission.HomeRead),
  );
}

export function canOpenPublisherDashboard(user: ShigoUser | undefined) {
  return Boolean(
    user &&
    hasShigoPermission(user.permissions, ShigoPermission.PublisherDashboardRead) &&
    (hasShigoPermission(user.permissions, ShigoPermission.PublisherDashboardHomeRead) ||
      hasShigoPermission(user.permissions, ShigoPermission.PublisherDashboardReportingRead)),
  );
}
